import { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { Project } from '../types';

interface ProjectCardProps {
  project: Project;
  onViewDetails: (project: Project) => void;
}

const ProjectCard = ({ project, onViewDetails }: ProjectCardProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="group relative glass-card rounded-2xl overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative h-56 overflow-hidden">
        <Image
          src={project.image}
          alt={project.alt}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div
          className={`absolute inset-0 bg-gradient-to-t from-[#020024] via-[#093679]/80 to-transparent transition-opacity duration-300 ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <div className="absolute bottom-0 left-0 right-0 p-5 transform transition-transform duration-300 translate-y-full group-hover:translate-y-0">
            <div className="flex gap-3">
              {project.liveUrl && (
                <button
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg gradient-cta text-white text-xs font-semibold hover:scale-105 transition-transform"
                  onClick={(e) => { e.stopPropagation(); window.open(project.liveUrl, '_blank'); }}
                >
                  <Icon name="ExternalLink" size={13} color="white" />
                  Live Demo
                </button>
              )}
              {project.githubUrl && (
                <button
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg glass border border-white/20 text-white text-xs font-semibold hover:border-accent/50 transition-all"
                  onClick={(e) => { e.stopPropagation(); window.open(project.githubUrl, '_blank'); }}
                >
                  <Icon name="Github" size={13} color="white" />
                  Code
                </button>
              )}
            </div>
          </div>
        </div>
        {project.featured && (
          <div className="absolute top-3 right-3 glass border border-accent/40 text-accent px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
            <Icon name="Star" size={12} color="currentColor" />
            Featured
          </div>
        )}
      </div>

      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-accent uppercase tracking-wider">
            {project.category}
          </span>
          <div className="flex gap-2">
            {project.liveUrl && (
              <Icon name="Globe" size={15} color="var(--color-accent)" className="opacity-50" />
            )}
            {project.githubUrl && (
              <Icon name="Github" size={15} color="var(--color-accent)" className="opacity-50" />
            )}
          </div>
        </div>

        <h3 className="text-xl font-bold text-white mb-2 line-clamp-2">
          {project.title}
        </h3>

        <p className="text-sm text-white/60 mb-4 line-clamp-3">
          {project.description}
        </p>

        <div className="flex flex-wrap gap-1.5 mb-5">
          {project.technologies.slice(0, 4).map((tech, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-accent/15 text-accent border border-accent/20 text-xs rounded-md font-medium"
            >
              {tech}
            </span>
          ))}
          {project.technologies.length > 4 && (
            <span className="px-2 py-1 bg-white/8 text-white/50 text-xs rounded-md font-medium">
              +{project.technologies.length - 4} more
            </span>
          )}
        </div>

        <button
          onClick={() => onViewDetails(project)}
          className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl glass border border-white/15 hover:border-accent/50 text-accent text-sm font-medium transition-all duration-300 hover:bg-accent/10"
        >
          View Case Study
          <Icon name="ArrowRight" size={15} color="currentColor" />
        </button>
      </div>
    </div>
  );
};

export default ProjectCard;